import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Field = ({ id, label, errors = {}, children }) => (
    <div>
        <Label htmlFor={id} className="text-sm">{label}</Label>
        {children}
        {errors[id] && <p className="text-xs text-red-500 mt-1">{errors[id]}</p>}
    </div>
);

/**
 * Selbstauskunft fuer Social Faces (ohne Login, per Token-Link):
 * Anschrift, PLZ, Ort, Land, IBAN, BIC, Kleinunternehmerregelung, abweichender Rechnungsname.
 */
const SocialFaceProfilePage = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({ street: '', postal_code: '', city: '', country: 'AT', iban: '', bic: '', is_small_business: false, billing_name: '' });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(null);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        axios.get(`${API_URL}/api/system/public-settings`).then(r => setSettings(r.data)).catch(() => {});
        axios.get(`${API_URL}/api/public/sf-profile/${token}`)
            .then(r => {
                setProfile(r.data);
                setForm({
                    street: r.data.street || '', postal_code: r.data.postal_code || '', city: r.data.city || '',
                    country: r.data.country || 'AT', iban: '', bic: r.data.bic || '',
                    is_small_business: !!r.data.is_small_business, billing_name: r.data.billing_name || '',
                });
            })
            .catch(e => setError(e.response?.data?.detail || 'Dieser Link ist ungültig oder abgelaufen.'))
            .finally(() => setLoading(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSaving(true);
        try {
            const payload = { ...form, iban: form.iban.trim() || null, bic: form.bic.trim() || null, billing_name: form.billing_name.trim() || null };
            const res = await axios.post(`${API_URL}/api/public/sf-profile/${token}`, payload);
            setDone(res.data);
        } catch (err) {
            const d = err.response?.data?.detail;
            if (d && typeof d === 'object' && d.errors) { setErrors(d.errors); toast.error(d.message || 'Bitte Eingaben prüfen'); }
            else toast.error(typeof d === 'string' ? d : 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
                <div className="text-center mb-6">
                    {settings?.logo_url ? (
                        <img src={settings.logo_url} alt="" className="w-14 h-14 mx-auto rounded-xl object-contain mb-3" />
                    ) : (
                        <div className="w-14 h-14 mx-auto rounded-xl bg-primary flex items-center justify-center text-white font-bold text-xl mb-3">A</div>
                    )}
                    <h1 className="text-2xl font-semibold tracking-tight">Deine Daten für die Abrechnung</h1>
                    <p className="text-muted-foreground text-sm mt-1">{settings?.company_name || 'TGOSM'} · Social Faces</p>
                </div>

                <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                    {loading ? (
                        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                    ) : error ? (
                        <div className="text-center py-6">
                            <p className="text-destructive font-medium">{error}</p>
                            <p className="text-sm text-muted-foreground mt-2">Bitte melde dich bei deiner Ansprechperson im Team, dann bekommst du einen neuen Link.</p>
                        </div>
                    ) : done ? (
                        <div className="text-center py-6" data-testid="sf-profile-done">
                            <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
                            <p className="font-medium">{done.message}</p>
                            {done.missing?.length > 0 ? (
                                <Button className="mt-4" variant="outline" onClick={() => setDone(null)}>Weiter ausfüllen</Button>
                            ) : (
                                <p className="text-sm text-muted-foreground mt-2">Du kannst diese Seite jetzt schließen.</p>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4" data-testid="sf-profile-form">
                            <p className="text-sm text-muted-foreground">
                                Hallo <strong className="text-foreground">{profile.first_name}</strong>, damit wir deine Shootings abrechnen und dein Honorar überweisen können, brauchen wir folgende Angaben.
                                {profile.missing?.length > 0 && <> Es fehlt noch: <strong className="text-foreground">{profile.missing.join(', ')}</strong>.</>}
                            </p>

                            <Field id="street" label="Straße und Hausnummer *" errors={errors}>
                                <Input id="street" value={form.street} onChange={(e) => setForm(f => ({ ...f, street: e.target.value }))} className="h-11 mt-1" autoComplete="street-address" required />
                            </Field>
                            <div className="grid grid-cols-[1fr_2fr] gap-3">
                                <Field id="postal_code" label="PLZ *" errors={errors}>
                                    <Input id="postal_code" value={form.postal_code} onChange={(e) => setForm(f => ({ ...f, postal_code: e.target.value }))} className="h-11 mt-1" inputMode="numeric" autoComplete="postal-code" required />
                                </Field>
                                <Field id="city" label="Ort *" errors={errors}>
                                    <Input id="city" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} className="h-11 mt-1" autoComplete="address-level2" required />
                                </Field>
                            </div>
                            <Field id="country" label="Land" errors={errors}>
                                <select id="country" value={form.country} onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
                                    <option value="AT">Österreich</option>
                                    <option value="DE">Deutschland</option>
                                    <option value="CH">Schweiz</option>
                                </select>
                            </Field>
                            <Field id="iban" errors={errors} label={profile.iban_set ? `IBAN (hinterlegt: ${profile.iban_masked}, nur ausfüllen, wenn sie sich geändert hat)` : 'IBAN *'}>
                                <Input id="iban" value={form.iban} onChange={(e) => setForm(f => ({ ...f, iban: e.target.value }))} className="h-11 mt-1 font-mono" placeholder="AT61 1904 3002 3457 3201" autoComplete="off" required={!profile.iban_set} />
                            </Field>
                            <Field id="bic" label="BIC (optional)" errors={errors}>
                                <Input id="bic" value={form.bic} onChange={(e) => setForm(f => ({ ...f, bic: e.target.value }))} className="h-11 mt-1 font-mono" placeholder="BKAUATWW" autoComplete="off" />
                            </Field>
                            <Field id="billing_name" label="Rechnungsname (nur falls abweichend, z. B. Firma)" errors={errors}>
                                <Input id="billing_name" value={form.billing_name} onChange={(e) => setForm(f => ({ ...f, billing_name: e.target.value }))} className="h-11 mt-1" />
                            </Field>
                            <label className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 cursor-pointer">
                                <Checkbox checked={form.is_small_business} onCheckedChange={(v) => setForm(f => ({ ...f, is_small_business: !!v }))} className="mt-0.5" />
                                <span className="text-sm">
                                    <span className="font-medium">Ich bin Kleinunternehmer:in</span>
                                    <span className="block text-muted-foreground text-xs">Kleinunternehmerregelung: keine Umsatzsteuer auf der Rechnung.</span>
                                </span>
                            </label>

                            <Button type="submit" className="w-full h-12 text-base" disabled={saving} data-testid="sf-profile-submit">
                                {saving ? 'Wird gespeichert...' : 'Speichern'}
                            </Button>
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1 justify-center">
                                <ShieldCheck className="w-3.5 h-3.5" /> Deine Daten werden nur für die Abrechnung verwendet.
                            </p>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default SocialFaceProfilePage;
